import React from 'react';
import { Theme } from '@material-ui/core/styles/createTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import Button from '@material-ui/core/Button';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import ArrowBackIos from '@material-ui/icons/ArrowBackIos';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/core/styles/makeStyles';
import withInfo from './Tooltip/withInfo';

const TooltipButton = withInfo(Button);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonWrapper: (props: Partial<Props>) => ({
      display: 'flex',
      position: 'relative',
      marginTop: theme.spacing(2),
      justifyContent: props.justifyContent ? props.justifyContent : 'center',
      '& button': {
        margin: theme.spacing(1, 1),
      },
      flexWrap: 'wrap',
      '@media (max-width: 400px)': {
        '& button': {
          width: '100%',
        },
      },
    }),
    buttonIcon: {
      marginRight: theme.spacing(1),
    },
    buttonIconLeft: {
      marginRight: theme.spacing(1),
    },
    buttonIconRight: {
      marginLeft: theme.spacing(1),
    },
    buttonProgress: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -12,
      marginLeft: -12,
    },
  }),
);

interface Props {
  form?: string;
  justifyContent?: 'start' | 'end' | 'center';
  isSubmitting: boolean;
  backText?: string;
  nextText?: string;
  onNext?: () => void;
  onBack?: () => void;
  fixedBack?: boolean;
  hideBack?: boolean;
  hideNext?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  backButtonId?: string;
  backDisabled?: boolean;
  backTooltipContent?: React.ReactElement;
  nextButtonId?: string;
  nextTooltipContent?: React.ReactElement;
  nextDisabled?: boolean;
  lastStep?: boolean;
  children?: React.ReactNode;
}

/**
 * ControlButtons are back and next buttons that can be used as a back and next or submit buttons
 * according to props. If the prop onNext is given to the component it will use it.
 * @prop  {string} [form] - optional - in case buttons are used out of form, form should be passed down for propper submit
 * @prop  {boolean} [isSubmitting] - optional - boolean which activate circular loading
 * @prop  {string} [backText] - optional - text for back button (default "back")
 * @prop  {string} [nextText] - optional - text for next button (default "next")
 * @prop  {boolean} [hideBack] - optional - hide back button
 * @prop  {boolean} [hideNext] - optional - hide next button
 * @prop  {() => void} [onNext] - optional - func that is passed to the next button, if it's given to the component it will make
 * next button of type="button" if the onNext is not used, next button will be of type="submit"
 * @prop  {() => void} [onBack] - optional - func passed to the back button
 * @prop  {'start' | 'end' | 'center'} [justifyContent] - optional - prop for managing the placement of the buttons (default "center")
 * @prop {() => void} [onClick] additional click event handler which doesnt fuck up with submit type
 */
const ControlButtons: React.FC<Props> = ({
  isSubmitting,
  backText,
  nextText,
  onNext,
  onBack,
  justifyContent,
  form,
  fixedBack,
  hideBack,
  hideNext,
  onClick,
  disabled,
  backButtonId,
  backTooltipContent,
  nextButtonId,
  nextTooltipContent,
  nextDisabled,
  backDisabled,
  lastStep,
  children,
}) => {
  const classes = useStyles({ justifyContent });
  const { t } = useTranslation();

  return (
    <div>
      <div className={classes.buttonWrapper}>
        {!hideBack && (
          <TooltipButton
            variant="contained"
            id={backButtonId || 'back-form-button'}
            onClick={onBack}
            disabled={disabled || backDisabled}
            tooltipContent={backTooltipContent}
            style={fixedBack ? { position: 'absolute', left: 0, marginLeft: 0 } : undefined}
          >
            <ArrowBackIos fontSize="small" className={classes.buttonIconLeft} />
            {backText || t('general_back')}
          </TooltipButton>
        )}
        {children}
        {!hideNext && (
          <TooltipButton
            type={onNext ? 'button' : 'submit'}
            form={form}
            onClick={onNext || onClick}
            disabled={isSubmitting || disabled || nextDisabled}
            variant="contained"
            color="primary"
            id={nextButtonId || 'submit-form-button'}
            tooltipContent={nextTooltipContent}
          >
            {nextText || t('general_next')}
            {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
            {!lastStep && <ArrowForwardIos fontSize="small" className={classes.buttonIconRight} />}
          </TooltipButton>
        )}
      </div>
    </div>
  );
};

export default ControlButtons;
